import { RecurrenceFrequency, RecommendationCategory } from "@/global/types";
import _i18n from "@/i18n";
import { i18n, TFunction } from "i18next";

export const offerLabel = (category: RecommendationCategory, t: any) => {
    // Loop is not used to be able to extract the keys for translation

    switch (category) {
        case RecommendationCategory.BANK_SERVICES: return t('offer_category.BANK_SERVICES');
        case RecommendationCategory.CREDIT_CARDS: return t('offer_category.CREDIT_CARDS');
        case RecommendationCategory.LOANS: return t('offer_category.LOANS');
        case RecommendationCategory.INSURANCE: return t('offer_category.INSURANCE');
        case RecommendationCategory.INVESTMENTS: return t('offer_category.INVESTMENTS');
        case RecommendationCategory.BUSINESS: return t('offer_category.BUSINESS');
        case RecommendationCategory.CASHBACK: return t('offer_category.CASHBACK');
        case RecommendationCategory.CASH_REWARDS: return t('offer_category.CASH_REWARDS');
        case RecommendationCategory.MORTGAGES: return t('offer_category.MORTGAGES');
        case RecommendationCategory.TAX_SAVINGS: return t('offer_category.TAX_SAVINGS');
        case RecommendationCategory.TRAVEL_PERKS: return t('offer_category.TRAVEL_PERKS');
        case RecommendationCategory.EDUCATION: return t('offer_category.EDUCATION');
        case RecommendationCategory.UTILITIES: return t('offer_category.UTILITIES');
        case RecommendationCategory.SUBSCRIPTIONS: return t('offer_category.SUBSCRIPTIONS');
        default: return category;
    }
}

/**
 * i18n key for a recurrence frequency, meant to be used with an interval count:
 * t(recurrenceLabelKey(frequency), { count: interval })
 */
export function recurrenceLabelKey(frequency: RecurrenceFrequency) {
    switch (frequency) {
        case RecurrenceFrequency.DAILY:
            return "every_n_days";
        case RecurrenceFrequency.WEEKLY:
            return "every_n_weeks";
        case RecurrenceFrequency.MONTHLY:
            return "every_n_months";
        case RecurrenceFrequency.YEARLY:
            return "every_n_years";
        default:
            return "unknown";
    }
};

export function getTranslationKey(value: string) {
    return value
        .normalize("NFKD")
        .replace(/[\/&]/g, "_")
        .replace(/\s+/g, "_")
        .replace(/_+/g, "_")
        .toLowerCase()
        .trim();
}

// categories and subcategories

type Index = Map<string, string>;

const categoryIndex: Index = new Map();
const subCategoryIndex: Index = new Map();

if (_i18n.hasResourceBundle('en', 'categories')) {
    buildCategoryIndex();
} else {
    _i18n.on('loaded', () => buildCategoryIndex());
}

export function buildCategoryIndex() {
    const bundle = _i18n.getResourceBundle('en', "categories") || {};

    for (const [catKey, catVal] of Object.entries(bundle)) {
        if (!catVal || typeof catVal !== "object") continue;

        const label = (catVal as any)._label;

        // category (label → category key)
        if (label) {
            categoryIndex.set(
                getTranslationKey(label),
                `categories:${catKey}._label`
            );
        }

        // subcategories (label → full path)
        for (const [subKey, subLabel] of Object.entries(catVal)) {
            if (subKey === "_label") continue;

            subCategoryIndex.set(
                getTranslationKey(subLabel as string),
                `categories:${catKey}.${subKey}`
            );
        }
    }
}

export function getCategoryKey(
    subCategory?: string | null,
    category?: string | null
): string | null {
    const sk = subCategory ? getTranslationKey(subCategory) : null;
    const ck = category ? getTranslationKey(category) : null;

    if (sk && subCategoryIndex.has(sk)) {
        return subCategoryIndex.get(sk)!;
    }

    if (ck && categoryIndex.has(ck)) {
        return categoryIndex.get(ck)!;
    }

    return null;
}

export function translateIfPossible(value: string, t: TFunction, i18nInstance: i18n = _i18n) {
    if (!value) return '-';

    const candidates: (string | null)[] = [
        getCategoryKey(value, null),
        getCategoryKey(null, value),
        getTranslationKey(value),
    ];

    for (const key of candidates) {
        if (!key) continue;
        if (i18nInstance.exists(key)) return t(key as any);
    }

    return value;
}
